/**
 * Singleton lazy do WorkerPoolSupervisor importado do wuweiAgent.
 *
 * - Uma única instância por aba do Messenger.
 * - O pool spawna workers ESM (whisper, text, clip, etc) sob demanda.
 * - O Vite resolve `?worker` dentro do pacote do Agent, então os workers
 *   ficam empacotados no `dist/` do Messenger sem extra config.
 * - O nome ("wuwei-messenger") aparece no monitor (BroadcastChannel
 *   "wuwei-agent-pool"), distinguindo-o de outras abas.
 */

import { WorkerPoolSupervisor } from "@wuwei-agent";
import { algumRecursoAtivo, bloqueioDaTool } from "./inferencia.js";

let poolInstance: WorkerPoolSupervisor | null = null;

export function getPool(): WorkerPoolSupervisor {
  // Barreira final: com a chave desligada nenhum worker nasce, então nenhum
  // modelo é baixado nem ocupa memória.
  if (!algumRecursoAtivo()) throw new Error("inferência local desligada");
  if (!poolInstance) {
    poolInstance = comChave(new WorkerPoolSupervisor({ name: "wuwei-messenger" }));
  }
  return poolInstance;
}

/**
 * Faz cada chave valer no último ponto em que ainda dá para não gastar memória.
 *
 * `algumRecursoAtivo()` acima é grosso demais desde que a tela passou a
 * oferecer quinze recursos: ligar "tom da mensagem" abria o pool para todos os
 * outros, e quem barrava cada tool era o cliente que a chamava — três lugares
 * hoje, um esquecimento amanhã. O modelo é baixado dentro de `runTool`, então
 * é aqui que a recusa custa zero.
 *
 * Envolver o método da instância, e não escrever a checagem em cada chamador,
 * é de propósito: caminho novo nenhum precisa lembrar de perguntar.
 */
function comChave(pool: WorkerPoolSupervisor): WorkerPoolSupervisor {
  const original = pool.runTool.bind(pool);
  (pool as unknown as { runTool: unknown }).runTool = (
    invocationId: string,
    toolId: string,
    input: unknown,
    opts?: unknown,
  ) => {
    const bloqueio = bloqueioDaTool(toolId);
    if (bloqueio) return Promise.reject(new Error(bloqueio));
    return original(invocationId, toolId, input as never, opts as never);
  };
  return pool;
}

/** Gera um ID único pra cada invocação enviada ao pool. */
export function newInvocationId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID().slice(0, 8)}-${Date.now().toString(36)}`;
}
